import { useEffect, useRef, useState } from 'react';

/**
 * Un menú que se abre al pulsar, no al pasar el ratón.
 *
 * Había tres escritos a mano —el selector de idioma, el menú de perfil y el de
 * exportar de Métricas— y los tres usaban el mismo truco de CSS:
 *
 *     opacity-0 invisible group-hover:opacity-100 group-hover:visible
 *
 * Sin `onClick` y sin foco. En un teléfono o una tableta no existe «pasar el
 * ratón», así que esos menús no se abrían: comprobado con emulación táctil, el
 * menú seguía en `visibility: hidden` después de tocarlo. Como el de perfil es
 * el que contiene **cerrar sesión**, desde un móvil no se podía salir de la
 * cuenta, ni cambiar el idioma, ni exportar.
 *
 * Arreglar los tres a mano habría dejado el mismo error esperando al cuarto.
 * Aquí el comportamiento vive una sola vez: se abre al pulsar, se cierra con
 * Escape, al pulsar fuera y al elegir una opción, y funciona con el teclado.
 *
 * ```jsx
 * <MenuDesplegable etiqueta={t('...')} boton={<HiUser />}>
 *     {(cerrar) => <button onClick={() => { cerrar(); hacerAlgo(); }}>…</button>}
 * </MenuDesplegable>
 * ```
 */
export default function MenuDesplegable({
    boton,
    etiqueta,
    children,
    anchura = 'w-48',
    claseBoton = '',
    alineacion = 'right-0'
}) {
    const [abierto, setAbierto] = useState(false);
    const contenedor = useRef(null);

    useEffect(() => {
        if (!abierto) return;

        const fuera = (e) => {
            if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false);
        };
        const escape = (e) => {
            if (e.key === 'Escape') setAbierto(false);
        };

        document.addEventListener('mousedown', fuera);
        document.addEventListener('keydown', escape);
        return () => {
            document.removeEventListener('mousedown', fuera);
            document.removeEventListener('keydown', escape);
        };
    }, [abierto]);

    return (
        <div className="relative" ref={contenedor}>
            <button
                type="button"
                onClick={() => setAbierto((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={abierto}
                aria-label={etiqueta}
                title={etiqueta}
                className={claseBoton}
            >
                {boton}
            </button>

            {abierto && (
                <div
                    role="menu"
                    className={`absolute ${alineacion} top-full mt-2 ${anchura} py-2 z-50 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700`}
                >
                    {typeof children === 'function' ? children(() => setAbierto(false)) : children}
                </div>
            )}
        </div>
    );
}
